import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { getMedicalCalculators } from "./actions";
import { MedicalCalculatorItem } from "../../../../interfaces/api/congresses";

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {new Array(10).fill(1).map((item, idx) => (
      <div
        key={idx}
        className="bg-white dark:bg-[#313131] rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
      >
        <div className="w-full px-6 py-4 flex items-center justify-between">
          <div className="h-6 bg-slate-200 dark:bg-black-600 rounded dark:opacity-[0.4] w-3/4 animate-pulse"></div>
          <div className="h-5 w-5 bg-slate-200 dark:bg-black-700 dark:opacity-[0.4] rounded animate-pulse"></div>
        </div>
      </div>
    ))}
  </div>
);

const MedicalCalculators = () => {
  const [calculators, setCalculators] = useState<MedicalCalculatorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    const fetchCalculators = async () => {
      const data = await getMedicalCalculators();
      setCalculators(data);
      setLoading(false);
    };

    fetchCalculators();
  }, []);

  const toggleAccordion = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Function to convert URLs in text to clickable links
  const formatTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split("\n").map((paragraph, index) => {
      if (!paragraph) return <br key={index} />;

      const parts = paragraph.split(urlRegex);
      return (
        <p key={index} className="text-slate-600 dark:text-slate-300 mb-4">
          {parts.map((part, i) => {
            if (part.match(urlRegex)) {
              return (
                <a
                  key={i}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark dark:text-primary-light dark:hover:text-primary underline"
                >
                  {part}
                </a>
              );
            }
            return part;
          })}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="py-4">
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="space-y-3">
        {calculators.map((item) => (
          <div
            key={item.id}
            className="bg-white dark:bg-[#313131] rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300 hover:border-slate-300 dark:hover:border-slate-600"
          >
            <button
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 dark:hover:bg-[#191919] transition-colors"
              onClick={() => toggleAccordion(item.id)}
            >
              <span className="text-slate-800 dark:text-slate-200 font-medium text-md">
                {item.title}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-slate-500 dark:text-slate-400 transition-transform duration-300 ease-in-out ${
                  expandedId === item.id ? "transform rotate-180" : ""
                }`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expandedId === item.id
                  ? "max-h-[1000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-6 py-4 bg-slate-50 dark:bg-[#191919] border-t border-slate-200 dark:border-slate-700 text-sm">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  {formatTextWithLinks(item.body)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MedicalCalculators;
